import {NotificationTypes} from '@/enums/notifier/NotificationTypes';

/**
 * Notification types of the settings dialog („Benachrichtigungen“ in the profile menu) and who sees which type.
 * Pure module (no store / Vue imports), checked in tests/notificationTypeOptions.check.ts.
 *
 * `audience`: interfaces (helpers/portalMode.ts) in which the type is offered – only types the login can actually receive
 * (notifier transmitters / senders in the services):
 * - office: all office types (unchanged)
 * - teacher (Lehrkraft-Bereich): Erwähnungen + Fehlende Protokolle (courseLogs transmitter, to the teacher's login). Substitution
 *   requests, takeover offers, payoff and trainings reach teachers by mail / WhatsApp, not as bell notification; the takeover
 *   notification (TAKEOVER_OFFER_UPDATE) only goes to the office (sender + Standort supervisor).
 * - applicant (Bewerbungs-Bereich): Erwähnungen only (applicationWithdrawn / applicantDeletionRequested go to the office).
 * Without `audience` a type is office-only. `teacherOnly` / `qualityOnly` additionally restrict the office view.
 */
export type NotificationAudience = 'office' | 'teacher' | 'applicant';

export type NotificationTypeOption = {
    type: NotificationTypes;
    text: string;
    icon: string;
    /** Default ['office'] */
    audience?: NotificationAudience[];
    /** Office view: only for logins whose employee is a teacher */
    teacherOnly?: boolean;
    /** Office view: only with read access to the QM-Cockpit */
    qualityOnly?: boolean;
};

/** Types shown in the settings, in this order */
export const NOTIFICATION_TYPE_OPTIONS: NotificationTypeOption[] = [
    {type: NotificationTypes.MENTION, text: 'Erwähnungen', icon: 'mdi-at', audience: ['office', 'teacher', 'applicant']},
    {type: NotificationTypes.CALL, text: 'Anrufe', icon: 'mdi-phone-incoming-outline'},
    {type: NotificationTypes.TO_DO_ASSIGNED, text: 'To-do zugewiesen', icon: 'mdi-clipboard-account-outline'},
    {type: NotificationTypes.DUE_TO_DO, text: 'To-do heute fällig', icon: 'mdi-calendar-today'},
    {type: NotificationTypes.OVERDUE_TO_DO, text: 'To-do überfällig', icon: 'mdi-alarm'},
    {type: NotificationTypes.TO_DO_COMPLETED, text: 'To-do erledigt', icon: 'mdi-check-circle-outline'},
    {
        type: NotificationTypes.MISSING_COURSE_LOGS, text: 'Fehlende Protokolle (morgens)', icon: 'mdi-clipboard-clock-outline',
        audience: ['office', 'teacher'], teacherOnly: true,
    },
    {type: NotificationTypes.QUALITY_CASE_DIGEST, text: 'QM-Cockpit: neue Fälle (Zusammenfassung)', icon: 'mdi-account-heart-outline', qualityOnly: true},
    {type: NotificationTypes.ENTITY_ASSIGNED_TO_SUPERVISOR, text: 'Neue Zuweisung', icon: 'mdi-account-arrow-left-outline'},
    {type: NotificationTypes.SUPERVISED_ENTITY_DEACTIVATED, text: 'Deaktivierung betreuter Profile', icon: 'mdi-account-off-outline'},
    {type: NotificationTypes.HANDOUT_EVENT_FEEDBACK, text: 'Feedback zu Austeilaktionen', icon: 'mdi-message-reply-text-outline'},
    {type: NotificationTypes.APPLICATION_WITHDRAWN, text: 'Bewerbung zurückgezogen', icon: 'mdi-account-arrow-left-outline'},
    {type: NotificationTypes.APPLICANT_DELETION_REQUESTED, text: 'Löschantrag (DSGVO) von Bewerber:innen', icon: 'mdi-account-cancel-outline'},
    {type: NotificationTypes.TAKEOVER_OFFER_UPDATE, text: 'Übernahme-Angebote (Zusagen, Absagen)', icon: 'mdi-account-convert-outline'},
    {type: NotificationTypes.TEACHER_SICK_REPORT, text: 'Ausfälle von Lehrkräften (Krankmeldung, zurückgezogene Vertretung)', icon: 'mdi-account-alert-outline'},
    {type: NotificationTypes.COURSE_CANCELLATION_FAILED, text: 'Fehler bei Kursabsagen', icon: 'mdi-alert-outline'},
    {type: NotificationTypes.WHATSAPP_MESSAGE_TEMPLATE_STATUS_UPDATE, text: 'WhatsApp-Vorlagen', icon: 'mdi-whatsapp'},
];

export type NotificationOptionContext = {
    /** portalMode(): 'office' | 'teacher' | 'applicant' | 'none' */
    mode: string;
    /** Employee of the login is a teacher (office view) */
    isTeacher?: boolean;
    /** Read access to the QM-Cockpit (office view) */
    canReadQuality?: boolean;
};

/** Types offered to a login (office: unchanged rules; Lehrkraft-/Bewerbungs-Bereich: only the types they receive) */
export function notificationTypeOptionsFor(context: NotificationOptionContext): NotificationTypeOption[] {
    const audience: NotificationAudience = context.mode === 'teacher' ? 'teacher' : context.mode === 'applicant' ? 'applicant' : 'office';
    return NOTIFICATION_TYPE_OPTIONS.filter((option) => {
        if (!(option.audience || ['office']).includes(audience)) {
            return false;
        }
        if (audience !== 'office') {
            return true;
        }
        return (!option.teacherOnly || !!context.isTeacher) && (!option.qualityOnly || !!context.canReadQuality);
    });
}
